import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { springStiff, springMedium, fadeShort, springBounce, springBounceDelay1, springBounceDelay2 } from "@/utils/motionVariants";

function generateData(referrals) {
  const months = [];
  let cumulative = 0;
  const avgMonthlyPerReferral = 20;
  for (let m = 1; m <= 12; m++) {
    const activeReferrals = Math.min(m * (referrals / 12), referrals);
    const monthly = Math.round(activeReferrals * avgMonthlyPerReferral);
    cumulative += monthly;
    months.push({ month: m, label: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1], monthly, cumulative, activeReferrals: Math.round(activeReferrals) });
  }
  return months;
}

const BAR_WIDTH = 32;
const CHART_H = 300;
const CHART_W = 600;
const PAD_LEFT = 55;
const PAD_BOTTOM = 50;
const PAD_TOP = 30;
const PAD_RIGHT = 10;

// Hoisted to avoid creating a fresh style object for every bar on every render.
const BAR_STYLE = { cursor: "pointer" };

function formatGBP(v) {
  if (v >= 10000) return `£${(v / 1000).toFixed(0)}k`;
  if (v >= 1000) return `£${(v / 1000).toFixed(1)}k`;
  return `£${v}`;
}

export default function SnowballGraph({ className = "" }) {
  const [referrals, setReferrals] = useState(10);
  const [hovered, setHovered] = useState(null);

  const data = useMemo(() => generateData(referrals), [referrals]);
  const maxCum = data[data.length - 1].cumulative || 1;
  const maxMonthly = Math.max(...data.map((d) => d.monthly)) || 1;
  const chartArea = CHART_H - PAD_BOTTOM - PAD_TOP;
  const barSpacing = (CHART_W - PAD_LEFT - PAD_RIGHT) / 12;

  // Bars scale to monthly max, line scales to cumulative max
  const barYScale = chartArea * 0.6 / maxMonthly; // bars take 60% height
  const lineYScale = chartArea / maxCum;

  // Y-axis gridlines for cumulative
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  const linePath = data.map((d, i) => {
    const x = PAD_LEFT + i * barSpacing + barSpacing / 2;
    const y = CHART_H - PAD_BOTTOM - d.cumulative * lineYScale;
    return `${i === 0 ? "M" : "L"}${x} ${y}`;
  }).join(" ");

  const areaPath = `${linePath} L${PAD_LEFT + 11 * barSpacing + barSpacing / 2} ${CHART_H - PAD_BOTTOM} L${PAD_LEFT + barSpacing / 2} ${CHART_H - PAD_BOTTOM} Z`;

  const hovData = hovered !== null ? data[hovered] : null;

  return (
    <div className={`bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden ${className}`} data-testid="snowball-graph">
      <div className="px-6 sm:px-8 py-5 border-b border-brand-light-grey/50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-bold text-brand-black">The Snowball Effect</h3>
            <p className="text-xs text-brand-grey">See how your residual income grows month by month</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-brand-black">Referrals/year:</label>
            <input
              type="range" min="1" max="50" value={referrals}
              onChange={(e) => setReferrals(parseInt(e.target.value, 10))}
              className="w-32 sm:w-40 h-8 bg-transparent cursor-pointer snowball-slider"
              style={{ touchAction: "none" }}
              data-testid="snowball-slider"
            />
            <span className="text-sm font-heading font-bold text-brand-pink min-w-[2rem] text-right">{referrals}</span>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="w-full" role="img" aria-label="Snowball income growth chart">
          {/* Y-axis gridlines + labels (cumulative scale) */}
          {yTicks.map((pct) => {
            const y = CHART_H - PAD_BOTTOM - pct * chartArea;
            const val = Math.round(pct * maxCum);
            return (
              <g key={pct}>
                <line x1={PAD_LEFT} y1={y} x2={CHART_W - PAD_RIGHT} y2={y} stroke="#e5e7eb" strokeWidth={0.5} strokeDasharray="4 4" />
                <text x={PAD_LEFT - 8} y={y + 3} textAnchor="end" fill="#9ca3af" fontSize={9} fontFamily="system-ui">{formatGBP(val)}</text>
              </g>
            );
          })}

          {/* X-axis line */}
          <line x1={PAD_LEFT} y1={CHART_H - PAD_BOTTOM} x2={CHART_W - PAD_RIGHT} y2={CHART_H - PAD_BOTTOM} stroke="#e5e7eb" strokeWidth={1} />

          {/* Bars — monthly income */}
          {data.map((d, i) => {
            const x = PAD_LEFT + i * barSpacing + (barSpacing - BAR_WIDTH) / 2;
            const barH = Math.max(d.monthly * barYScale, 2);
            const y = CHART_H - PAD_BOTTOM - barH;
            const isHov = hovered === i;
            return (
              <g key={d.month} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} onClick={() => setHovered(hovered === i ? null : i)} style={BAR_STYLE}>
                {/* Hover highlight column */}
                <rect x={PAD_LEFT + i * barSpacing} y={PAD_TOP} width={barSpacing} height={chartArea} fill={isHov ? "#f0f9f7" : "transparent"} />
                <motion.rect
                  x={x} width={BAR_WIDTH} rx={4}
                  initial={{ y, height: barH }}
                  animate={{ y, height: barH }}
                  transition={springStiff}
                  fill={isHov ? "#e84393" : "#69c0ab"}
                  opacity={isHov ? 1 : 0.75}
                />
                {/* Bar value label */}
                <motion.text
                  x={x + BAR_WIDTH / 2}
                  animate={{ y: y - 6 }}
                  transition={springStiff}
                  textAnchor="middle" fill="#4a5568" fontSize={8} fontWeight="bold" fontFamily="system-ui"
                  opacity={isHov ? 1 : 0.6}
                >
                  {`£${d.monthly}`}
                </motion.text>
                {/* Month label */}
                <text x={x + BAR_WIDTH / 2} y={CHART_H - PAD_BOTTOM + 14} textAnchor="middle" fill={isHov ? "#e84393" : "#9ca3af"} fontSize={9} fontWeight={isHov ? "bold" : "normal"} fontFamily="system-ui">{d.label}</text>
                {/* Active referrals count */}
                <text x={x + BAR_WIDTH / 2} y={CHART_H - PAD_BOTTOM + 26} textAnchor="middle" fill="#d1d5db" fontSize={7} fontFamily="system-ui">{`${d.activeReferrals} ref`}</text>
              </g>
            );
          })}

          {/* Cumulative area fill */}
          <motion.path
            d={areaPath}
            fill="url(#snowballGradient)"
            animate={{ opacity: 0.12 }}
            transition={fadeShort}
          />

          {/* Cumulative line */}
          <motion.path
            d={linePath}
            fill="none"
            stroke="#e84393"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ d: linePath }}
            animate={{ d: linePath }}
            transition={springMedium}
          />

          {/* Data points on line */}
          {data.map((d, i) => {
            const x = PAD_LEFT + i * barSpacing + barSpacing / 2;
            const y = CHART_H - PAD_BOTTOM - d.cumulative * lineYScale;
            const isHov = hovered === i;
            return (
              <motion.circle
                key={`dot-${d.month}`}
                cx={x}
                initial={{ cy: y }}
                animate={{ cy: y }}
                transition={springStiff}
                r={isHov ? 6 : 3.5}
                fill={isHov ? "#e84393" : "white"}
                stroke="#e84393"
                strokeWidth={2}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => setHovered(hovered === i ? null : i)}
                style={{ cursor: "pointer" }}
              />
            );
          })}

          {/* Gradient definition */}
          <defs>
            <linearGradient id="snowballGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e84393" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#e84393" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Legend */}
          <g transform={`translate(${PAD_LEFT}, ${CHART_H - 8})`}>
            <rect width={10} height={10} rx={2} fill="#69c0ab" opacity={0.75} />
            <text x={14} y={9} fill="#9ca3af" fontSize={8} fontFamily="system-ui">Monthly income</text>
            <line x1={90} y1={5} x2={105} y2={5} stroke="#e84393" strokeWidth={2} />
            <circle cx={97.5} cy={5} r={2.5} fill="#e84393" />
            <text x={110} y={9} fill="#9ca3af" fontSize={8} fontFamily="system-ui">Cumulative total</text>
          </g>
        </svg>
      </div>

      {/* Hover detail panel */}
      <div className="px-6 sm:px-8 py-3 bg-brand-black text-white min-h-[52px] flex items-center justify-center">
        {hovData ? (
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm">
            <span><strong className="text-brand-aqua">{hovData.label}</strong></span>
            <span>Monthly: <strong className="text-white">{`£${hovData.monthly}`}</strong></span>
            <span>Cumulative: <strong className="text-brand-pink">{`£${hovData.cumulative.toLocaleString()}`}</strong></span>
            <span>Active referrals: <strong className="text-white">{hovData.activeReferrals}</strong></span>
          </div>
        ) : (
          <p className="text-xs text-white/40">Hover over a month to see detailed breakdown</p>
        )}
      </div>

      {/* Summary stats */}
      <div className="px-6 sm:px-8 py-4 bg-gray-50 border-t border-brand-light-grey/50 grid grid-cols-3 gap-4 text-center">
        <div>
          <motion.p
            className="text-xl sm:text-2xl font-heading font-bold text-brand-aqua-dark"
            key={`monthly-${referrals}`}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={springBounce}
          >
            {`£${data[data.length - 1].monthly}`}
          </motion.p>
          <p className="text-[10px] text-brand-grey uppercase tracking-wider">Monthly by Dec</p>
        </div>
        <div>
          <motion.p
            className="text-xl sm:text-2xl font-heading font-bold text-brand-pink"
            key={`total-${referrals}`}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={springBounceDelay1}
          >
            {`£${data[data.length - 1].cumulative.toLocaleString()}`}
          </motion.p>
          <p className="text-[10px] text-brand-grey uppercase tracking-wider">Total Year 1</p>
        </div>
        <div>
          <motion.p
            className="text-xl sm:text-2xl font-heading font-bold text-brand-black"
            key={`ref-${referrals}`}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={springBounceDelay2}
          >
            {referrals}
          </motion.p>
          <p className="text-[10px] text-brand-grey uppercase tracking-wider">Referrals</p>
        </div>
      </div>
    </div>
  );
}
